import { FC, useMemo, useState } from "react";
import CustomLabel from "../common/CustomLabel";
import Select from "react-select";
import { socialMediaOptions } from "./resources";
import { Socials } from "../../types";
import { SocialMediaIcons } from "../../utils/SocialMediaIcons";

const SocialMediaSelector: FC<SocialMediaSelectorProps> = ({
  value,
  setValue,
  labelText,
  disabled = false,
}) => {
  const [selectedPlatform, setSelectedPlatform] = useState<
    keyof Socials | null
  >(null);
  const [inputValue, setInputValue] = useState<string>("");

  const availableOptions = socialMediaOptions.filter(
    (option) => !value[option.value]
  );

  const handleAddSocial = () => {
    if (selectedPlatform && inputValue.trim()) {
      setValue({
        ...value,
        [selectedPlatform]: inputValue,
      });
      resetInputFields();
    }
  };

  const handleRemoveSocial = (platform: keyof Socials) => {
    setValue({ ...value, [platform]: "" });
  };

  const resetInputFields = () => {
    setSelectedPlatform(null);
    setInputValue("");
  };

  const hasHandle = useMemo(() => Object.values(value).some(Boolean), [value]);

  return (
    <>
      <CustomLabel labelText={labelText} />
      {hasHandle && (
        <div className="space-y-2">
          {Object.entries(value).map(([platform, handle]) => {
            const socialPlatform = platform as keyof Socials;

            return (
              handle && (
                <div
                  key={socialPlatform}
                  className="flex items-center justify-between bg-gray-1 border rounded-md px-2"
                >
                  <div className="flex items-center font-medium text-sm py-2">
                    <span className="border-r border-gray-3 pr-1 mr-4">
                      <SocialMediaIcons platform={socialPlatform} />
                    </span>
                    {handle}
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      handleRemoveSocial(platform as keyof Socials)
                    }
                    className="text-danger text-sm font-semibold hover:font-[900] py-2"
                  >
                    ✕
                  </button>
                </div>
              )
            );
          })}
        </div>
      )}

      {/* Changed from flex to flex-col on small screens and row on md and up */}
      <div className="flex flex-col md:flex-row md:items-stretch gap-2">
        <Select
          components={{ IndicatorSeparator: () => null }}
          isSearchable={false}
          className="font-medium text-sm leading-4 cursor-pointer w-full md:w-48"
          placeholder="Select social"
          options={availableOptions}
          value={
            availableOptions.find(
              (option) => option.value === selectedPlatform
            ) || null
          }
          onChange={(selectedOption) =>
            setSelectedPlatform(selectedOption?.value || null)
          }
          isDisabled={disabled}
        />

        <input
          type="text"
          placeholder="Enter username or link"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          className="font-medium text-sm leading-4 text-gray-7 border border-gray-3 rounded-md py-2 px-3 outline-none focus:ring-1 focus:ring-black focus:border-transparent w-full md:w-auto"
          onKeyDown={(e) => {
            if (e.key === "Enter") handleAddSocial();
          }}
          disabled={disabled}
        />

        <button
          type="button"
          onClick={handleAddSocial}
          className={`text-white px-4 py-2 rounded-lg ${
            disabled ? "bg-gray-4" : "bg-gray-7"
          } w-full md:w-auto`}
          disabled={disabled}
        >
          Add
        </button>
      </div>
    </>
  );
};

export default SocialMediaSelector;

interface SocialMediaSelectorProps {
  value: Socials;
  setValue: (updatedSocials: Socials) => void;
  labelText: string;
  disabled?: boolean;
}
